/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { PRICE_RANGE_FRAGMENT } from './PriceRangeFragment';
import { CUSTOMIZABLE_OPTIONS_FRAGMENT } from './CustomizableOptionsFragment';
import { DOWNLOADABLE_CART_ITEMS_FRAGMENT } from './DownloadableCartItemsFragment';
import {
  GIFT_MESSAGE_FRAGMENT,
  GIFT_WRAPPING_FRAGMENT,
  AVAILABLE_GIFT_WRAPPING_FRAGMENT,
} from './GiftFragment';

export const CART_ITEM_FRAGMENT = /* GraphQL */ `
  fragment CART_ITEM_FRAGMENT on CartItemInterface {
    __typename
    uid
    quantity
    is_available
    not_available_message
    errors {
      code
      message
    }

    prices {
      price {
        value
        currency
      }
      discounts {
        amount {
          value
          currency
        }
        label
      }
      total_item_discount {
        value
        currency
      }
      row_total {
        value
        currency
      }
      row_total_including_tax {
        value
        currency
      }
      price_including_tax {
        value
        currency
      }
      fixed_product_taxes {
        amount {
          value
          currency
        }
        label
      }
      original_item_price {
        value
        currency
      }
      original_row_total {
        value
        currency
      }
    }

    product {
      name
      sku
      price_tiers {
        quantity
        final_price {
          value
        }
        discount {
          amount_off
          percent_off
        }
      }
      quantity
      gift_message_available
      gift_wrapping_available
      gift_wrapping_price {
        currency
        value
      }
      thumbnail {
        url
        label
      }
      url_key
      canonical_url
      categories {
        url_path
        url_key
        name
      }
      custom_attributesV2(filters: { is_visible_on_front: true }) {
        items {
          code
          ... on AttributeValue {
            value
          }
          ... on AttributeSelectedOptions {
            selected_options {
              value
              label
            }
          }
        }
      }
      only_x_left_in_stock
      stock_status
      price_range {
        ...PRICE_RANGE_FRAGMENT
      }
    }
    ... on SimpleCartItem {
      available_gift_wrapping {
        ...AVAILABLE_GIFT_WRAPPING_FRAGMENT
      }
      gift_message {
        ...GIFT_MESSAGE_FRAGMENT
      }
      gift_wrapping {
        ...GIFT_WRAPPING_FRAGMENT
      }
      customizable_options {
        ...CUSTOMIZABLE_OPTIONS_FRAGMENT
      }
    }
    ... on ConfigurableCartItem {
      available_gift_wrapping {
        ...AVAILABLE_GIFT_WRAPPING_FRAGMENT
      }
      gift_message {
        ...GIFT_MESSAGE_FRAGMENT
      }
      gift_wrapping {
        ...GIFT_WRAPPING_FRAGMENT
      }
      configurable_options {
        configurable_product_option_uid
        option_label
        value_label
        configurable_product_option_value_uid
      }
      configured_variant {
        uid
        sku
        only_x_left_in_stock
        stock_status
        thumbnail {
          label
          url
        }
        price_range {
          ...PRICE_RANGE_FRAGMENT
        }
        price_tiers {
          quantity
          final_price {
            value
          }
          discount {
            amount_off
            percent_off
          }
        }
      }
      customizable_options {
        ...CUSTOMIZABLE_OPTIONS_FRAGMENT
      }
    }
    ...DOWNLOADABLE_CART_ITEMS_FRAGMENT
    ... on BundleCartItem {
      available_gift_wrapping {
        ...AVAILABLE_GIFT_WRAPPING_FRAGMENT
      }
      gift_message {
        ...GIFT_MESSAGE_FRAGMENT
      }
      gift_wrapping {
        ...GIFT_WRAPPING_FRAGMENT
      }
      bundle_options {
        uid
        label
        values {
          uid
          label
        }
      }
    }
    ... on GiftCardCartItem {
      message
      recipient_email
      recipient_name
      sender_email
      sender_name
      amount {
        currency
        value
      }
      is_available
    }
  }

  ${PRICE_RANGE_FRAGMENT}
  ${CUSTOMIZABLE_OPTIONS_FRAGMENT}
  ${DOWNLOADABLE_CART_ITEMS_FRAGMENT}
  ${GIFT_WRAPPING_FRAGMENT}
  ${GIFT_MESSAGE_FRAGMENT}
  ${AVAILABLE_GIFT_WRAPPING_FRAGMENT}
`;
